import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Table,
  Input,
} from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import Box from "../Pokemon/Box";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getMiningLevel } from "../../constants/items";
import "../Items/ManageItem.css";
const Profile = () => {
  document.title = "Pokémon Radiance | Player Profile";

  const [activeTab, setActiveTab] = useState("main");

  const [profile, setProfile] = useState(null);
  const [roster, setRoster] = useState([]);
  const [box, setBox] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [logs, setLogs] = useState([]);

  const [inventorySearch, setInventorySearch] = useState("");
  const [logsSearch, setLogsSearch] = useState("");
  const [logsFilter, setLogsFilter] = useState("all");
  const [rankings, setRankings] = useState(null);

  const username = localStorage.getItem("authUser") || null;

  // Fetch main profile data immediately
  useEffect(() => {
    Promise.all([
      api.get("/v1/profile", { withCredentials: true }),
      api.get("/v1/roster", { withCredentials: true }),
      api.get("/v1/pokemon", { withCredentials: true }),
      api.get("/v1/inventory", { withCredentials: true }),
      api.get("/v1/gamelog", { withCredentials: true }),
    ])
      .then(([profileRes, rosterRes, boxRes, inventoryRes, logRes]) => {
        setProfile(profileRes.data);
        setRoster(rosterRes.data);
        setBox(boxRes.data);
        setInventory(inventoryRes.data.filter((item) => item.amount > 0));
        setLogs(logRes.data);
      })
      .catch((err) => {
        console.error("Error loading profile data:", err);
      });
  }, [username]);

  useEffect(() => {
    const now = Date.now();
    const stored = localStorage.getItem("leaderboardData");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // 12 hours in milliseconds
        if (parsed.timestamp && now - parsed.timestamp < 12 * 60 * 60 * 1000) {
          setRankings(parsed.data);
          return;
        }
      } catch (e) {
        console.error("Error parsing leaderboard data:", e);
      }
    }
    // If no valid cached data, fetch the rankings in the background.
    api
      .get(`/v1/leaderboard/rankings?username=${username}`, {
        withCredentials: true,
      })
      .then((res) => {
        const dataToStore = { data: res.data, timestamp: Date.now() };
        localStorage.setItem("leaderboardData", JSON.stringify(dataToStore));
        setRankings(res.data);
      })
      .catch((err) => console.error("Error fetching rankings:", err));
  }, [username]);

  const filteredInventory = useMemo(() => {
    return inventory.filter((item) =>
      item.item_name.toLowerCase().includes(inventorySearch.toLowerCase()),
    );
  }, [inventory, inventorySearch]);

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const searchMatch = log.info
        .toLowerCase()
        .includes(logsSearch.toLowerCase());
      const filterMatch =
        logsFilter === "all" ||
        log.who.toLowerCase() === logsFilter.toLowerCase();
      return searchMatch && filterMatch;
    });
  }, [logs, logsSearch, logsFilter]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  function getLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  }
  function getCatcherLevel(exp) {
    const lvl = Math.floor(Math.pow(exp, 1 / 2.5));
    return Math.pow(lvl + 1, 2.5) <= exp ? lvl + 1 : lvl;
  }

  // getAwardImage now returns the proper image for each rank.
  const getAwardImage = (rank) => {
    if (rank === 1) {
      return (
        <img
          src={`/items/1st.png`}
          alt="1st Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    } else if (rank === 2) {
      return (
        <img
          src={`/items/2nd.png`}
          alt="2nd Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    } else if (rank === 3) {
      return (
        <img
          src={`/items/3rd.png`}
          alt="3rd Award"
          style={{
            width: "24px",
            verticalAlign: "middle",
            marginLeft: "0.5rem",
          }}
        />
      );
    }
    return null;
  };

  const getOrdinalRank = (rank) => {
    const j = rank % 10,
      k = rank % 100;
    if (j === 1 && k !== 11) return `${rank}st`;
    if (j === 2 && k !== 12) return `${rank}nd`;
    if (j === 3 && k !== 13) return `${rank}rd`;
    return `${rank}th`;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Profile" breadcrumbItem="Player Profile" />
          <Row>
            <Col md={12}>
              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "main" })}
                    onClick={() => toggleTab("main")}
                    style={{ cursor: "pointer" }}
                  >
                    Main
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({
                      active: activeTab === "inventory",
                    })}
                    onClick={() => toggleTab("inventory")}
                    style={{ cursor: "pointer" }}
                  >
                    Inventory
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "box" })}
                    onClick={() => toggleTab("box")}
                    style={{ cursor: "pointer" }}
                  >
                    Box
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === "log" })}
                    onClick={() => toggleTab("log")}
                    style={{ cursor: "pointer" }}
                  >
                    Logs
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab} className="p-3">
                {/* Main Tab */}
                <TabPane tabId="main">
                  <Card
                    style={{
                  
                      borderRadius: "8px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
                      marginBottom: "1rem",
                    }}
                  >
                    <CardBody style={{ borderRadius: "12px", padding: "2rem" }}>
                      <Row className="align-items-center">
                        <Col md="2" className="text-center">
                          <img
                            src={
                              profile
                                ? `/avatars/${profile.avatar}.png`
                                : "/loading.gif"
                            }
                            alt="Avatar"
                            style={{
                              width: "90px",
                              borderRadius: "50%",
                              border: "3px  #fff",
                            }}
                          />
                        </Col>
                       <Col md="10">
  <div className="mb-3">
    {profile ? (
      (() => {
        // Define staff members and their roles
        const staffRoles = {
          'Brokeboy': 'Developer',
          'ZigCiguzo': 'Developer', 
          'JoeD2Cold': 'Admin',
          'EmoNovember': 'Moderator'
        };
        
        const userRole = staffRoles[profile.username];
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <h3 className="text-white mb-0">{profile.username}</h3>
            <span style={{
              backgroundColor: userRole ? (
                userRole === 'Developer' ? '#E91E63' : 
                userRole === 'Admin' ? '#FF5722' : '#9C27B0'
              ) : '#6c757d',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '16px',
              fontSize: '0.85rem',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}>
              {userRole || 'Trainer'}
            </span>
          </div>
        );
      })()
    ) : (
      <h3 className="text-white mb-0">Loading...</h3>
    )}
  </div>

  <div className="mb-3">
    {profile ? (
      <>
        {/* Currency & Pokédex - Compact Layout */}
        <div
          style={{
            background: "linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: "8px",
            padding: "10px 14px",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "16px",
            marginBottom: "12px"
          }}
        >
          {/* Coins */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/items/coin.png" alt="coin" style={{ width: "20px", height: "20px" }} />
            <strong style={{ color: "#eab308" }}>{profile.coins.toLocaleString()}</strong>
          </div>

          {/* Battle Coins */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/items/battle_coin.png" alt="battle coin" style={{ width: "20px", height: "20px" }} />
            <strong style={{ color: "#ffffff" }}>{profile.battle_coins.toLocaleString()}</strong>
          </div>

          {/* Money */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/items/money.png" alt="money" style={{ width: "20px", height: "20px" }} />
            <strong style={{ color: "#22c55e" }}>{profile.money.toLocaleString()}</strong>
          </div>

          {/* Pokédex */}
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img src="/items/pokedex.png" alt="pokedex" style={{ width: "20px", height: "20px" }} />
            <span style={{ color: "#3b82f6", fontWeight: "600" }}>{profile.pokedex_count.toLocaleString()} Species</span>
          </div>
        </div>

        {/* Levels in a professional grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "10px"
          }}
        >
          {/* Trainer Level */}
          <div style={{
            background: "linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.05) 100%)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: "8px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            cursor: "default"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(239, 68, 68, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            <img
              src="/items/battleseek.png"
              alt="trainer level"
              style={{ width: "24px", height: "24px" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase", fontWeight: "600" }}>Trainer</span>
              <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#ef4444" }}>Lv. {getLevel(profile.exp).toLocaleString()}</span>
            </div>
          </div>

          {/* Catcher Level */}
          <div style={{
            background: "linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.05) 100%)",
            border: "1px solid rgba(34, 197, 94, 0.3)",
            borderRadius: "8px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            cursor: "default"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(34, 197, 94, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            <img
              src="/items/pokeball.png"
              alt="catcher level"
              style={{ width: "24px", height: "24px" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase", fontWeight: "600" }}>Catcher</span>
              <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#22c55e" }}>Lv. {getCatcherLevel(profile.catcher_exp).toLocaleString()}</span>
            </div>
          </div>

          {/* Mining Level */}
          <div style={{
            background: "linear-gradient(135deg, rgba(161, 98, 7, 0.15) 0%, rgba(120, 53, 15, 0.05) 100%)",
            border: "1px solid rgba(161, 98, 7, 0.3)",
            borderRadius: "8px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            cursor: "default"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(161, 98, 7, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            <img
              src="/items/NormalPickaxe.png"
              alt="mining level"
              style={{ width: "24px", height: "24px" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase", fontWeight: "600" }}>Mining</span>
              <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#d97706" }}>Lv. {getMiningLevel(profile.miningexp).toLocaleString()}</span>
            </div>
          </div>

          {/* Fishing Level */}
          <div style={{
            background: "linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.05) 100%)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            cursor: "default"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            <img
              src="/items/superrod.png"
              alt="fishing level"
              style={{ width: "24px", height: "24px" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase", fontWeight: "600" }}>Fishing</span>
              <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#3b82f6" }}>Lv. {getMiningLevel(profile.fishingexp).toLocaleString()}</span>
            </div>
          </div>

          {/* Farming Level */}
          <div style={{
            background: "linear-gradient(135deg, rgba(251, 146, 60, 0.15) 0%, rgba(249, 115, 22, 0.05) 100%)",
            border: "1px solid rgba(251, 146, 60, 0.3)",
            borderRadius: "8px",
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "all 0.2s ease",
            cursor: "default"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(251, 146, 60, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
          >
            <img
              src="/items/razzberry.png"
              alt="farming level"
              style={{ width: "24px", height: "24px" }}
            />
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <span style={{ fontSize: "0.7rem", color: "rgba(255, 255, 255, 0.6)", textTransform: "uppercase", fontWeight: "600" }}>Farming</span>
              <span style={{ fontSize: "0.95rem", fontWeight: "700", color: "#fb923c" }}>Lv. {getMiningLevel(profile.farmingexp).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </>
    ) : (
      "Loading profile details..."
    )}
  </div>

  <div className="mt-4">
    {rankings ? (
      <div className="mt-3">
        <h5 className="mb-2">Leaderboard Rankings</h5>
        <Row>
          <Col md="4">
            Trainer:{" "}
            {rankings.trainer.rank <= 3
              ? getAwardImage(rankings.trainer.rank)
              : getOrdinalRank(rankings.trainer.rank)}
          </Col>
          <Col md="4">
            Catcher:{" "}
            {rankings.catcher.rank <= 3
              ? getAwardImage(rankings.catcher.rank)
              : getOrdinalRank(rankings.catcher.rank)}
          </Col>
          <Col md="4">
            Mining:{" "}
            {rankings.mining.rank <= 3
              ? getAwardImage(rankings.mining.rank)
              : getOrdinalRank(rankings.mining.rank)}
          </Col>
        </Row>
      </div>
    ) : (
      <div style={{ color: "#aaa" }}>
        <img src="/loading.gif" alt="Loading..." width="32" /> Loading rankings...
      </div>
    )}
  </div>
</Col>

                      </Row>
                    </CardBody>
                  </Card>

                  <h5 style={{
                    color: "#fff",
                    marginBottom: "1rem",
                    paddingLeft: "0.5rem",
                    borderLeft: "3px solid rgba(16, 185, 129, 0.8)"
                  }}>Party</h5>
                  <Row>
                    {roster.length > 0 ? (
                      roster.map((poke) => (
                        <Col key={poke.id} md="3" sm="6" className="mb-3">
                          <Card
                            style={{
                              borderRadius: "12px",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                              border: "none",
                              overflow: "hidden",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                              cursor: "pointer",
                              backgroundColor: "#1d222e"
                            }}
                            className="hover-effect"
                          >
                            <CardBody className="text-center" style={{
                              backgroundColor: "#242836",
                              padding: "1.25rem"
                            }}>
                              <img
                                src={`/sprites/${poke.name}.png`}
                                alt={poke.name}
                                style={{ width: "120px" }}
                              />
                              <h6 style={{ 
                                marginTop: "0.75rem",
                                color: "#ffffff",
                                fontWeight: "600"
                              }}>
                                {poke.nickname || poke.name}
                              </h6>
                              <div style={{ 
                                fontSize: "0.9rem",
                                color: "rgba(255, 255, 255, 0.8)",
                                marginTop: "0.5rem"
                              }}>
                                <div style={{
                                  backgroundColor: "rgba(16, 185, 129, 0.2)",
                                  color: "#10b981",
                                  borderRadius: "4px",
                                  padding: "0.25rem 0.5rem",
                                  display: "inline-block",
                                  fontWeight: "500"
                                }}>
                                  Level: {getLevel(poke.totalexp).toLocaleString()}
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        </Col>
                      ))
                    ) : (
                      <div style={{ 
                        color: "rgba(255, 255, 255, 0.7)",
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        borderRadius: "8px",
                        padding: "1.5rem",
                        textAlign: "center",
                        border: "1px solid rgba(255, 255, 255, 0.1)"
                      }}>
                        <i className="ri-ghost-line" style={{ fontSize: "2rem", marginBottom: "0.5rem" }}></i>
                        <p>No Pokémon in your roster.</p>
                      </div>
                    )}
                  </Row>
                </TabPane>

                {/* Inventory Tab */}
                <TabPane tabId="inventory">
                  <div className="equipment-container">
                    <div className="equipment-header">
                      <h1 className="equipment-title">Inventory</h1>
                      <p className="equipment-subtitle">View your item collection</p>
                    </div>

                    <div className="inventory-section">
                      <div className="inventory-header">
                        <div className="search-container">
                          <input
                            type="text"
                            className="search-input"
                            placeholder="Search items..."
                            value={inventorySearch}
                            onChange={(e) => setInventorySearch(e.target.value)}
                          />
                          <div className="search-icon">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="11" cy="11" r="8"></circle>
                              <path d="21 21l-4.35-4.35"></path>
                            </svg>
                          </div>
                        </div>
                      </div>

                      <div className="inventory-grid">
                        {filteredInventory.map((item) => (
                          <div key={item.item} className="inventory-item">
                            <img
                              src={item.item_image}
                              alt={item.item_name}
                              className="inventory-item-image"
                            />
                            <div className="inventory-item-name">{item.item_name}</div>
                            <div className="inventory-item-quantity">
                              {Number(item.amount).toLocaleString()}
                            </div>
                          </div>
                        ))}
                      </div>

                      {filteredInventory.length === 0 && (
                        <div style={{ textAlign: 'center', color: '#b8c5d6', padding: '40px' }}>
                          <p>No items found</p>
                        </div>
                      )}
                    </div>
                  </div>
                </TabPane>

                {/* Box Tab */}
                <TabPane tabId="box">
                  <div style={{ marginTop: "-5rem" }}>
                    {activeTab === "box" && <Box />}
                  </div>
                </TabPane>

                {/* Logs Tab */}
                <TabPane tabId="log">
                  <Card>
                    <CardBody>
                      <Row className="mb-3">
                        <Col md="4">
                          <Input
                            type="text"
                            placeholder="Search Logs..."
                            value={logsSearch}
                            onChange={(e) => setLogsSearch(e.target.value)}
                          />
                        </Col>
                        <Col md="4">
                          <select
                            className="form-control"
                            value={logsFilter}
                            onChange={(e) => setLogsFilter(e.target.value)}
                          >
                            <option value="all">All Types</option>
                            <option value="pokemon rankings">
                              Pokemon Rankings
                            </option>
                            <option value="map rankings">Map Rankings</option>
                            <option value="trainer rankings">
                              Trainer Rankings
                            </option>
                            <option value="auction house">Auction House</option>
                            <option value="sabrina sold">Sabrina Sold</option>
                            <option value="gift sent">Gift Sent</option>
                            <option value="color box">Color Box</option>
                            <option value="server">Server</option>
                          </select>
                        </Col>
                      </Row>
                      <Table striped responsive className="align-middle">
                        <thead>
                          <tr>
                            <th>When</th>
                            <th>Who</th>
                            <th>Info</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredLogs.length > 0 ? (
                            filteredLogs.map((log) => (
                              <tr key={log.id}>
                                <td>
                                  {new Date(log.when * 1000).toLocaleString()}
                                </td>
                                <td>{log.who}</td>
                                <td>{log.info}</td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="3" className="text-center">
                                No logs found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Profile;
